import React, {
    Dispatch,
    SetStateAction,
    useCallback,
    useEffect,
    useId,
    useRef,
    useState,
} from 'react';
import { Blocker, useBlocker } from 'react-router-dom';
import { BlockerFunction } from '@remix-run/router/router';

type ContextType = {
    register: (id: string, check: BlockerFunction, onProceedFn?: () => void) => void;
    unregister: (id: string) => void;
    shouldBlock: BlockerFunction;
    blocker: Blocker | null;
    setBlocker: Dispatch<SetStateAction<Blocker | null>>;
    proceed: () => void;
    reset: () => void;
};

const NavigationBlockerContext = React.createContext<ContextType>({} as ContextType);

export const useNavigationBlockerContext = () => {
    return React.useContext(NavigationBlockerContext);
};

export const NavigationBlockerContextProvider = ({ children }) => {
    const conditionsRef = useRef(new Map());
    const onProceedFnsRef = useRef(new Map());
    const [blocker, setBlocker] = useState<Blocker | null>(null);

    const register = (id: string, check: BlockerFunction, onProceedFn?: () => void) => {
        conditionsRef.current.set(id, check);
        if (onProceedFn) {
            onProceedFnsRef?.current.set(id, onProceedFn);
        }
    };

    const unregister = (id: string) => {
        conditionsRef.current.delete(id);
        if (onProceedFnsRef.current.has(id)) {
            onProceedFnsRef?.current.delete(id);
        }
    };

    const shouldBlock: BlockerFunction = ({ currentLocation, nextLocation }) => {
        const prev = currentLocation.pathname.split('/').filter(Boolean).at(-1);
        const next = nextLocation.pathname.split('/').filter(Boolean).at(-1);

        if (
            currentLocation.pathname === nextLocation.pathname ||
            (prev === 'new' && next === 'edit')
        ) {
            return false;
        }
        for (const check of conditionsRef.current.values()) {
            if (check()) {
                return true;
            }
        }
        return false;
    };

    const proceed = useCallback(() => {
        for (const onProceedFn of onProceedFnsRef.current.values()) {
            onProceedFn();
        }
        blocker?.proceed?.();
    }, [blocker]);

    const reset = useCallback(() => {
        blocker?.reset?.();
    }, [blocker]);

    const value = {
        register,
        unregister,
        shouldBlock,
        blocker,
        setBlocker,
        proceed,
        reset,
    };

    return (
        <NavigationBlockerContext.Provider value={value}>
            {children}
        </NavigationBlockerContext.Provider>
    );
};

export function useGlobalRouteBlocker() {
    const { shouldBlock, setBlocker } = useNavigationBlockerContext();

    const b = useBlocker(shouldBlock);

    useEffect(() => {
        if (b?.state === 'blocked') {
            setBlocker(b);
        }

        if (b?.state === 'unblocked') {
            setBlocker(null);
        }
    }, [b, setBlocker]);

    return null;
}

export function useConditionalBlocker({
    checkFn,
    onProceedFn,
}: {
    checkFn: BlockerFunction;
    onProceedFn?: () => void;
}) {
    const id = useId();
    const { register, unregister } = useNavigationBlockerContext();

    useEffect(() => {
        register(id, checkFn, onProceedFn);
        return () => {
            unregister(id);
        };
    }, [id, checkFn, register, unregister]);
}
